import { ProvisionType } from '@wix/ambassador-funnel-intro-v1-meta-site-provision-descriptor/types'
import { Box, Button, InputImperativeActions, Loader } from '@wix/design-system'
import { useContent, useMetadata, useNext, usePageLoading } from '@wix/funnel-builder-lizard'
import { retry, tryCatch } from '@wix/funnel-utils'
import React, { FC, useEffect, useRef, useState } from 'react'

import { provisionMetasite } from '../../../api/FunnelIntroSiteProvisioningService/provisionMetasite'
import { suggestDomains } from '../../../api/suggestDomains'
import { useDebouncedTypingLogger, usePageLogger } from '../../../bi/mfi'
import { resolveCategoryFromBusinessTerm } from '../../../category'
import { useErrorNotification } from '../../../components/ErrorNotification/ErrorNotificationContext'
import { useStorageContext } from '../../../context/StorageContext/StorageContext'
import { DesignSystemTokensLoader } from '../../reusable/DesignSystemTokensLoader'
import { WixMobileBackButton } from '../../reusable/WixMobileBackButton/WixMobileBackButton'
import { WixMobileChip } from '../../reusable/WixMobileChip'
import { WixMobileContentWrapper } from '../../reusable/WixMobileContentWrapper/WixMobileContentWrapper'
import { WixMobileDescription } from '../../reusable/WixMobileDescription/WixMobileDescription'
import { WixMobileFooter } from '../../reusable/WixMobileFooter/WixMobileFooter'
import { WixMobileHeader } from '../../reusable/WixMobileHeader/WixMobileHeader'
import { WixMobileHeading } from '../../reusable/WixMobileHeading/WixMobileHeading'
import { WixMobileInput } from '../../reusable/WixMobileInput'
import { WixMobilePage } from '../../reusable/WixMobilePage/WixMobilePage'
import { WixMobileSkipButton } from '../../reusable/WixMobileSkipButton/WixMobileSkipButton'
import { WixMobileStepProgressBar } from '../../reusable/WixMobileStepProgressBar/WixMobileStepProgressBar'
import { TWixMobileBusinessName as Content } from './WixMobileBusinessName.content'
import { wixMobileBusinessNameDataHooks as dataHooks } from './WixMobileBusinessName.dataHooks'
import s from './WixMobileBusinessName.scss'

// Values without an entry (e.g. 'none') provision nothing; a new provisioning
// kind must be mapped here explicitly instead of falling through to a wrong type
const PROVISION_TYPE_BY_CONTENT: Partial<Record<NonNullable<Content['provisioning']>, ProvisionType>> = {
  'dashboard-first': ProvisionType.DASHBOARD_FIRST,
}

export const WixMobileBusinessName: FC = () => {
  const { content } = useContent<Content>()
  const { questionKey } = content.question
  const provisionType = PROVISION_TYPE_BY_CONTENT[content.provisioning ?? 'dashboard-first']
  const { save, appDefIds = [], businessName: previousBusinessName, category, nameSuggestions } = useStorageContext()
  const [businessName, setBusinessName] = useState(previousBusinessName || '')
  const answerTypeRef = useRef<'chips' | 'free text'>('free text')
  const [pageLoading] = usePageLoading()
  const pageLogger = usePageLogger()
  const debouncedTyping = useDebouncedTypingLogger(pageLogger)
  const next = useNext()
  const { showError } = useErrorNotification()
  const [isSaveSiteDetailsInProgress, setIsSaveSiteDetailsInProgress] = useState(false)
  const [{ biSlug: flowBiSlug }] = useMetadata()
  const inputRef = useRef<InputImperativeActions<HTMLInputElement>>()

  const provisionMetaSite = async (type: ProvisionType) => {
    const { industryId, structureId, displayName, postRegVertical, businessTermId, businessSegmentId } =
      resolveCategoryFromBusinessTerm(category?.businessTerm, category?.businessSegmentId)

    const response = await provisionMetasite({
      type,
      origin: flowBiSlug,
      metadata: {
        flow: flowBiSlug,
      },
      additionalIntroData: {
        businessName,
        industryId,
        structureId,
        category: {
          industryId,
          structureId,
          displayName,
          postRegVertical,
          businessTermId,
          businessSegmentId,
        },
        selectedApps: appDefIds.map(appDefIds => ({ appDefId: appDefIds })),
      },
    })

    const metaSiteId = response.data.metaSiteProvisionDescriptor?.metaSiteId || undefined

    save({ provisionedMetaSiteId: metaSiteId })

    return metaSiteId
  }

  const suggestDomain = async () => {
    if (!businessName || content.suggestDomains === false) {
      return
    }

    const response = await suggestDomains({ query: businessName, limit: 1 })
    const suggestion = response.data.suggestions?.[0]
    save({ suggestedDomain: suggestion?.domain || '' })
  }

  const onBackButtonClick = () => {
    void pageLogger.generalAction({ action: 'back' })
    window.history.back()
  }

  const onSkipButtonClick = async () => {
    if (pageLoading || isSaveSiteDetailsInProgress) {
      return
    }

    if (!provisionType) {
      void pageLogger.skip()
      next({ [questionKey]: { skipped: true } })
      return
    }

    setIsSaveSiteDetailsInProgress(true)

    const [error, metaSiteId] = await tryCatch(() => provisionMetaSite(provisionType))

    if (error) {
      showError()
    } else {
      void pageLogger.skip()
      next({ [questionKey]: { skipped: true } }, { metaSiteId })
    }

    setIsSaveSiteDetailsInProgress(false)
  }

  const onNextButtonClick = async () => {
    if (pageLoading || isSaveSiteDetailsInProgress) {
      return
    }

    setIsSaveSiteDetailsInProgress(true)

    // won't do anyting if clicked on next button, but will blur if enter is pressed
    inputRef?.current?.blur()
    save({ businessName })

    const suggestedDomainPromise = tryCatch(() => retry(() => suggestDomain()))

    if (!provisionType) {
      await suggestedDomainPromise
      if (!businessName) {
        void pageLogger.skip()
        next({ [questionKey]: { skipped: true } })
        return
      }
      void pageLogger.answer({
        answer: businessName,
        answerType: answerTypeRef.current,
        freeText: businessName,
      })
      next({ [questionKey]: { values: [businessName], skipped: false } })
      return
    }

    const provisionMetasitePromise = tryCatch(() => provisionMetaSite(provisionType))
    const [[error, metaSiteId]] = await Promise.all([provisionMetasitePromise, suggestedDomainPromise])

    if (!error) {
      if (!businessName) {
        await pageLogger.skip()
        next({ [questionKey]: { skipped: true } }, { metaSiteId })
        return
      }

      void pageLogger.answer({
        answer: businessName,
        answerType: answerTypeRef.current,
        freeText: businessName,
      })
      next({ [questionKey]: { values: [businessName], skipped: !businessName } }, { metaSiteId })
      return
    }

    showError()
    setIsSaveSiteDetailsInProgress(false)
  }

  useEffect(() => {
    void pageLogger.screenView()
  }, [pageLogger])

  useEffect(() => {
    inputRef.current?.focus()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const suggestionsRef = useRef<HTMLDivElement>()
  // What this effect does - prevents losing focus on input on click on suggestion
  useEffect(() => {
    const el = suggestionsRef.current
    if (!el) {
      return
    }
    const options = { capture: true }
    const handler = (e: PointerEvent) => {
      if (e.target instanceof HTMLSpanElement) {
        e.preventDefault()
        e.stopPropagation()
      }
    }
    el.addEventListener('pointerdown', handler, options)
    return () => {
      el.removeEventListener('pointerdown', handler, options)
    }
  }, [])

  const handleSuggestionClick = (sugg: string, index: number) => {
    answerTypeRef.current = 'chips'
    void pageLogger.clickAnswer({ questionKey, buttonName: sugg, suggestedOption: true, position: index + 1 })
    setBusinessName(sugg)
  }

  useEffect(() => {
    inputRef.current?.input?.setAttribute('enterkeyhint', 'next')
  }, [])

  return (
    <DesignSystemTokensLoader type="odeditor">
      <WixMobilePage>
        <WixMobileHeader
          skin="harmony"
          leftContent={<WixMobileBackButton onBack={onBackButtonClick} dataHook={dataHooks.back()} />}
          rightContent={
            (content.skip && (
              <WixMobileSkipButton dataHook={dataHooks.skip()} onSkip={onSkipButtonClick} label={content.skip} />
            )) ||
            undefined
          }
        />
        <WixMobileContentWrapper>
          <WixMobileStepProgressBar
            current={content.stepProgressBar.content.current}
            total={content.stepProgressBar.content.total}
            skin="harmony"
            dataHook={dataHooks.stepProgressBar()}
          />
          <WixMobileHeading dataHook={dataHooks.title()} skin="harmony">
            {content.question.content.title}
          </WixMobileHeading>
          <WixMobileDescription dataHook={dataHooks.subtitle()} skin="harmony">
            <span id="description">{content.question.content.subtitle}</span>
          </WixMobileDescription>
          <WixMobileInput
            ref={inputRef as any}
            value={businessName}
            onChange={e => {
              answerTypeRef.current = 'free text'
              setBusinessName(e.target.value)
              debouncedTyping(e.target.value)
            }}
            placeholder={content.question.content.placeholder}
            dataHook={dataHooks.input()}
            clearButton
            onClear={() => {
              answerTypeRef.current = 'free text'
              setBusinessName('')
            }}
            size="medium"
            onEnterPressed={onNextButtonClick}
          />
          {!!nameSuggestions?.length && (
            <Box
              direction="horizontal"
              gap="8px"
              flexWrap="wrap"
              align="center"
              padding="20px 0 0"
              ref={suggestionsRef}
            >
              {nameSuggestions.map((suggestion, index) => (
                <WixMobileChip
                  key={suggestion}
                  id={suggestion}
                  onClick={() => handleSuggestionClick(suggestion, index)}
                  size="medium"
                  removable={false}
                >
                  {suggestion}
                </WixMobileChip>
              ))}
            </Box>
          )}
        </WixMobileContentWrapper>
        <WixMobileFooter>
          {content.primaryBtn && (
            <Button
              size="large"
              fullWidth
              onClick={onNextButtonClick}
              dataHook={dataHooks.primaryBtn()}
              className={s.finishButton}
            >
              <Box gap="SP1" verticalAlign="middle">
                {isSaveSiteDetailsInProgress ? <Loader size="tiny" /> : content.primaryBtn}
              </Box>
            </Button>
          )}
        </WixMobileFooter>
      </WixMobilePage>
    </DesignSystemTokensLoader>
  )
}
